import React from 'react';
import { colors, font, spacing, radius } from '../../styles/tokens';

interface Props {
  children: React.ReactNode;
  /** Label shown in the error card (e.g. page name). */
  name?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors in child components and shows a recovery card
 * instead of crashing the entire dashboard.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.name ? ` ${this.props.name}` : ''}]`, error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          backgroundColor: colors.bg.secondary,
          border: `1px solid ${colors.accent.red}33`,
          borderRadius: radius.lg,
          padding: spacing.xl,
          margin: spacing.lg,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 18 }}>!</span>
            <span style={{ fontSize: font.size.lg, fontWeight: font.weight.semibold, color: colors.accent.red }}>
              {this.props.name ? `${this.props.name} crashed` : 'Something went wrong'}
            </span>
          </div>
          <pre style={{
            fontSize: font.size.sm,
            color: colors.text.muted,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: '8px 0 16px',
            maxHeight: 120,
            overflow: 'auto',
          }}>
            {this.state.error.message}
          </pre>
          <button
            onClick={() => this.setState({ error: null })}
            style={{
              background: colors.bg.tertiary,
              color: colors.text.primary,
              border: `1px solid ${colors.border.default}`,
              borderRadius: radius.md,
              padding: '6px 16px',
              fontSize: font.size.md,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
